/**
 * One named function per `/v1` operation this application uses.
 *
 * # Why this layer exists rather than calling the client from components
 *
 * Three reasons, in order of how much they matter.
 *
 * It is the enforcement point. `eslint.config.js` bans a `/v1` path literal
 * everywhere except `src/api/client.ts`, so a component physically cannot
 * invent an endpoint; it has to add a function here, where the path is checked
 * against the generated contract and a reviewer can see the whole API surface
 * the frontend depends on in one file.
 *
 * It is where "this endpoint does not exist" is written down. Several obvious
 * operations have no route — listing repositories, creating one, listing the
 * organizations a user belongs to — and the comments at the bottom of this file
 * record that, with the evidence, so the next person does not spend an
 * afternoon looking for them.
 *
 * It keeps the pagination envelope in one place. Every collection in this API
 * answers `{ data, next_cursor }`, and a component that destructures that
 * itself is a component that will get it wrong once.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';
import { ApiError } from './problem';

type Schemas = components['schemas'];

export type User = Schemas['User'];
export type Organization = Schemas['Organization'];
export type Repository = Schemas['Repository'];
export type ActivityEntry = Schemas['ActivityEntry'];
export type Notification = Schemas['Notification'];
export type SecuritySummary = Schemas['SecuritySummary'];
export type Token = Schemas['Token'];
export type MFAMethod = Schemas['MFAMethod'];

export interface UserOrganization extends Organization {
  role: string;
}

/** The envelope every collection response uses. */
export interface Page<T> {
  data: T[];
  next_cursor: string | null;
}

/**
 * Normalise a collection response.
 *
 * The generated type for a list is an intersection of the `Page` envelope with
 * a narrowing of `data`, and TypeScript surfaces that as a shape whose `data`
 * is `unknown[] & T[]`. Reading it through one function keeps the awkwardness
 * here instead of at eleven call sites, and gives every list the same
 * `{ items, nextCursor }` the UI wants.
 */
function page<T>(value: { data?: unknown; next_cursor?: unknown }): Page<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

/* ====================================================================== *
 * Authentication
 * ====================================================================== */

/**
 * Exchange credentials for a session.
 *
 * Answers `204` and sets an `HttpOnly` cookie; there is no body and no token to
 * read. The caller learns who signed in by calling `getCurrentUser` afterwards,
 * which is the only way — and is also the check that the cookie was actually
 * accepted by the browser, which matters in development, where
 * `SESSION_COOKIE_SECURE` must be `false` over plain HTTP or the browser
 * silently drops the cookie and every subsequent call is a 401.
 */
export function signIn(body: { email: string; password: string }): Promise<void> {
  return expectNoContent(http.POST('/auth/sign-in', { body }));
}

export function signOut(): Promise<void> {
  return expectNoContent(http.POST('/auth/sign-out', {}));
}

/**
 * Present the second factor.
 *
 * Reachable by a session that `internal/api/auth.go` refuses for everything
 * else; a TOTP code and a recovery code are both accepted here and the server
 * does not say which it matched.
 */
export function verifyMFA(body: { code: string }): Promise<void> {
  return expectNoContent(http.POST('/auth/mfa/verify', { body }));
}

/* ====================================================================== *
 * The signed-in person
 * ====================================================================== */

/**
 * The caller.
 *
 * `personal_org_id` is the field the whole application pivots on: there is no
 * route that lists the organizations a user belongs to, so this is the only
 * organization identifier the frontend can obtain without being handed one.
 */
export function getCurrentUser(): Promise<User> {
  return expect(http.GET('/users/me', {}));
}

export async function listTokens(): Promise<Page<Token>> {
  return page<Token>(await expect(http.GET('/users/me/tokens', {})));
}

export async function listMFAMethods(): Promise<{
  methods: MFAMethod[];
  recovery_codes_remaining: number;
}> {
  const result = await expect(http.GET('/users/me/mfa', {}));
  return {
    methods: result.methods,
    recovery_codes_remaining: result.recovery_codes_remaining,
  };
}

export async function listMyOrganizations(): Promise<Page<UserOrganization>> {
  return page<UserOrganization>(await expect(http.GET('/users/me/organizations', {})));
}

/* ====================================================================== *
 * Organization
 * ====================================================================== */

export function getOrganization(orgId: string): Promise<Organization> {
  return expect(http.GET('/organizations/{org_id}', { params: { path: { org_id: orgId } } }));
}

export type SecurityPolicy = Schemas['SecurityPolicy'];

/**
 * The organization's security policy, or `null` when none has been published.
 *
 * A 404 is turned into `null` rather than thrown, because the contract lists
 * 404 as an ordinary response here: an organization that has never had a policy
 * published has no policy, and that is an answer. It is also, in this build,
 * the *expected* answer — nothing in the running system can publish one, which
 * the Security page says in as many words rather than leaving the reader to
 * assume their policy failed to load.
 */
export async function getSecurityPolicy(orgId: string): Promise<SecurityPolicy | null> {
  try {
    return await expect(
      http.GET('/organizations/{org_id}/security/policy', {
        params: { path: { org_id: orgId } },
      }),
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/* ====================================================================== *
 * The personal, organization-scoped surface
 *
 * These four are what the first screen of Exi Git is built from, because they
 * are what exists. See the note at the bottom of this file.
 * ====================================================================== */

export async function listFavoriteRepositories(
  orgId: string,
  limit = 30,
): Promise<Page<Repository>> {
  return page<Repository>(
    await expect(
      http.GET('/organizations/{org_id}/me/favorites', {
        params: { path: { org_id: orgId }, query: { limit } },
      }),
    ),
  );
}

export async function listWatchedRepositories(
  orgId: string,
  limit = 30,
): Promise<Page<Repository>> {
  return page<Repository>(
    await expect(
      http.GET('/organizations/{org_id}/me/watching', {
        params: { path: { org_id: orgId }, query: { limit } },
      }),
    ),
  );
}

export async function listPersonalActivity(
  orgId: string,
  options: { limit?: number; cursor?: string } = {},
): Promise<Page<ActivityEntry>> {
  return page<ActivityEntry>(
    await expect(
      http.GET('/organizations/{org_id}/me/activity', {
        params: {
          path: { org_id: orgId },
          query: {
            limit: options.limit ?? 30,
            ...(options.cursor ? { cursor: options.cursor } : {}),
          },
        },
      }),
    ),
  );
}

export async function listNotifications(
  orgId: string,
  options: { unread?: boolean; limit?: number; cursor?: string } = {},
): Promise<Page<Notification>> {
  return page<Notification>(
    await expect(
      http.GET('/organizations/{org_id}/me/notifications', {
        params: {
          path: { org_id: orgId },
          query: {
            limit: options.limit ?? 30,
            ...(options.unread === undefined ? {} : { unread: options.unread }),
            ...(options.cursor ? { cursor: options.cursor } : {}),
          },
        },
      }),
    ),
  );
}

export function acknowledgeNotification(
  orgId: string,
  notificationId: string,
): Promise<Notification> {
  return expect(
    http.POST('/organizations/{org_id}/me/notifications/{notification_id}/read', {
      params: { path: { org_id: orgId, notification_id: notificationId } },
    }),
  );
}

/* ====================================================================== *
 * Repository-scoped reads
 *
 * Reachable only for a repository the caller already has the id of, which in
 * this build means one that came back from favourites or watching.
 * ====================================================================== */

export function getSecuritySummary(orgId: string, repoId: string): Promise<SecuritySummary> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/security/summary', {
      params: { path: { org_id: orgId, repo_id: repoId } },
    }),
  );
}

export function setFavorite(orgId: string, repoId: string): Promise<void> {
  return expectNoContent(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/favorite', {
      params: { path: { org_id: orgId, repo_id: repoId } },
    }),
  );
}

export function clearFavorite(orgId: string, repoId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/favorite', {
      params: { path: { org_id: orgId, repo_id: repoId } },
    }),
  );
}

export function setWatch(orgId: string, repoId: string): Promise<void> {
  return expectNoContent(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/watch', {
      params: { path: { org_id: orgId, repo_id: repoId } },
    }),
  );
}

export function clearWatch(orgId: string, repoId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/watch', {
      params: { path: { org_id: orgId, repo_id: repoId } },
    }),
  );
}

/* ====================================================================== *
 * Phase 1: repositories, Git content, pull requests, issues and tokens
 *
 * In `repo.ts` for length, re-exported here so that this module stays the
 * one place the application's API surface is enumerated.
 * ====================================================================== */

export * from './repo';

/* ====================================================================== *
 * Phase 2: teams, branches, protection, the pull request and issue
 * additions, labels, milestones, projects, the inbox, search, the dashboard
 * and the audit log. In `phase2.ts` for the same reason.
 * ====================================================================== */

export * from './phase2';

/* ====================================================================== *
 * Phase 3: workflow runs, releases, deployments, insights, organization
 * settings and webhooks. In `delivery.ts` for the same reason.
 * ====================================================================== */

export * from './delivery';

/* ====================================================================== *
 * Phase 4: sessions and security history, identity provider, roles, usage,
 * findings triage, incidents, traces, the platform description. In
 * `phase4.ts` for the same reason.
 * ====================================================================== */

export * from './phase4';
export * from './aireview';
export * from './aireviewrules';
export * from './failureanalysis';
export * from './automation';

/* ====================================================================== *
 * Operations that still do not exist
 *
 * Verified against `internal/api/api.go`
 * (`grep -oP 's\.route\(\s*"\K[^"]+' internal/api/api.go`).
 *
 *   GET /v1/organizations                       — list every organization the
 *       caller belongs to. Deliberately absent: it is a query across tenants.
 *       The application reaches an organization by slug instead
 *       (`getMyOrganizationBySlug`, which answers only for members).
 *   PATCH /v1/users/me                          — the profile is read-only.
 * ====================================================================== */
export * from './securityIntel';
export * from './knowledge';
export * from './integrations';
export * from './governance';
export * from './runners';

/* ====================================================================== *
 * Observability & Traceability (DL-245): the service registry, SLOs,
 * telemetry reads and the lineage reads the Traceability tree needs. In
 * their own files for the same reason as the phases above.
 * ====================================================================== */

export * from './services';
export * from './slos';
export * from './telemetry';
export * from './lineage';
